<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { ShiftExpense } from '~/schemas/shiftExpenses'

const { t } = useI18n()
const dialogRef = inject('dialogRef') as any
const shiftId = computed(() => dialogRef?.value?.data?.shiftId)
const expenseStore = useExpenseStore()
const { expenseTypes, fundSources } = storeToRefs(expenseStore)

const expenseTypeId = ref<number | null>(null)
const expenseTypeError = ref('')
const fundSourceId = ref<number | null>(null)
const fundSourceError = ref('')

watch(expenseTypeId, (v) => {
  if (v) expenseTypeError.value = ''
})

watch(fundSourceId, (v) => {
  if (v) fundSourceError.value = ''
})

async function shiftExpenseSubmit(formData: ShiftExpense): Promise<void> {
  if (!expenseTypeId.value) {
    expenseTypeError.value = t('Expense type is required')
    return
  }
  expenseTypeError.value = ''

  if (!fundSourceId.value) {
    fundSourceError.value = t('Fund source is required')
    return
  }
  fundSourceError.value = ''

  const label = expenseTypes.value.find(tp => tp.value === expenseTypeId.value)?.label ?? ''
  const success = await expenseStore.createExpense({
    ...formData,
    shift_id: shiftId.value,
    expense_id: expenseTypeId.value,
    fund_source_id: fundSourceId.value,
    expense_type: label,
  })
  if (success) {
    dialogRef.value?.close()
  }
}

onMounted(() => {
  expenseStore.fetchExpenseTypes()
  expenseStore.fetchFundSources()
})
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    id="order-details"
    class="flex"
    type="form"
    :disabled="!shiftId"
    :actions="false"
    @submit="shiftExpenseSubmit"
  >
    <div class="flex gap-3 justify-center">
      <div class="w-1/2">
        <FormKit
          type="text"
          :label="t('Shift ID')"
          number
          placeholder="0"
          :disabled="true"
          :value="shiftId"
          validation="required"
          name="shift_id"
        />
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('Expense type') }}</label>
          <FormExpenseTypeSelect
            v-model="expenseTypeId"
            :options="expenseTypes"
            :allow-create="false"
          />
          <small
            v-if="expenseTypeError"
            class="text-red-500"
          >{{ expenseTypeError }}</small>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('Fund source') }}</label>
          <FormFundSourceSelect
            v-model="fundSourceId"
            :options="fundSources"
            :allow-create="false"
          />
          <small
            v-if="fundSourceError"
            class="text-red-500"
          >{{ fundSourceError }}</small>
        </div>
        <FormKit
          type="text"
          :label="t('Amount')"
          placeholder="0.00"
          validation="required|number"
          validation-visibility="live"
          name="amount"
        />
        <FormKit
          type="textarea"
          cols="20"
          :label="t('Notes')"
          name="notes"
        />
        <FormKit
          type="submit"
          :label="t('Add expense')"
        />
      </div>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
